import React, { Component } from 'react'
import { connect } from 'react-redux'

import {
  Animated,
  Easing,
  StyleSheet,
  View,
  Platform,
  Linking,
} from 'react-native'

import { NAVIGATION_BACK, transitions } from '@adalo/constants'

import { BackHandler } from 'utils/history'
import {
  TARGET_FORGOT_PASSWORD,
  TARGET_RESET_PASSWORD,
} from 'utils/forgotPassword'
import { getScreenSize } from 'utils/responsive'

import { register } from 'utils/notification-wrapper'
import { createStack, push, peek, pop } from 'utils/stacks'
import { registerDevice, parseURL } from 'utils/notifications'
import { TRANSITION_CONFIG } from 'utils/transitions'
import { unsafeGetToken } from 'ducks/auth'
import { showNotification } from 'ducks/notifications'
import { resetRandomNumbers } from 'ducks/random'

import Screen from './Screen'
import ErrorScreen from './Error'
import { ForgotPassword, ResetPassword } from './ForgotPassword'
import AppContext from '../contexts/AppContext'
import Loading from './Loading'

export const SLIDE_TRANSITIONS = [
  transitions.SLIDE_LEFT,
  transitions.SLIDE_RIGHT,
  transitions.SLIDE_UP,
  transitions.SLIDE_DOWN,
]

export const FLOAT_TRANSITIONS = [
  transitions.FLOAT_LEFT,
  transitions.FLOAT_RIGHT,
  transitions.FLOAT_UP,
  transitions.FLOAT_DOWN,
]

class Navigator extends Component {
  static contextType = AppContext

  linkingEventListener = null

  constructor(props, context) {
    super(props)
    const { updateContext } = context
    updateContext({
      navigate: this.navigate,
      getDeviceId: this.getDeviceId,
      getParams: this.getParams,
    })
    this.setupRoutes(props)
  }

  getDeviceId = () => {
    const { deviceId } = this.state
    return deviceId
  }

  handleRegister = async deviceId => {
    const { app, authenticated } = this.props
    const { getNotificationsURL, isPreviewer } = this.context

    if (deviceId && deviceId.token) {
      deviceId = deviceId.token
    }

    this.setState({ deviceId })

    if (authenticated) {
      await registerDevice(
        app,
        deviceId,
        getNotificationsURL(),
        unsafeGetToken(null, app),
        isPreviewer
      )
    }
  }

  handleNotification = (route, notif = null) => {
    if (notif) {
      return this.handleForegroundNotification(route, notif)
    } else {
      return this.handleNotificationSub(route)
    }
  }

  handleNotificationSub = route => {
    // handle
    const { initialComponentId } = this.props

    let { routeStack } = this.state

    if (!routeStack) {
      routeStack = createStack({
        target: initialComponentId,
        params: {},
      })
    }

    if (route.target !== routeStack[0].target) {
      routeStack = push(routeStack, this.getRoute(route), false)
    }

    this.setState({
      routeStack,
    })
  }

  handleForegroundNotification = (route, notif) => {
    const { showNotification } = this.props

    showNotification(notif, () => this.handleNotificationSub(route))
  }

  getRoute(route) {
    return {
      params: {},
      transition: transitions.PUSH,
      ...route,
    }
  }

  setupRoutes = (initialProps = null) => {
    const props = initialProps || this.props
    const { initialRoute, deviceId } = props

    let routeStack = createStack({
      target: props.initialComponentId,
      params: {},
      transition: null,
    })

    if (initialRoute) {
      routeStack = push(routeStack, this.getRoute(initialRoute))
    }

    const newState = {
      routeStack,
      deviceId,
      baseOffset: -1,
      navigationInProgress: false,
      currentViewOffset: new Animated.Value(0),
      loadingScreen: false,
    }

    this.notify(peek(routeStack))

    if (initialProps !== null) {
      this.state = newState
    } else {
      this.setState(newState)
    }
  }

  isAndroid = () => {
    const { getTransitionStyle } = this.context
    const transitionStyle = getTransitionStyle && getTransitionStyle()
    return (transitionStyle || Platform.OS) === 'android'
  }

  getTiming = transitionType => {
    if (transitionType === transitions.NONE) {
      return 0
    }

    return 300
  }

  goBack = (...args) => {
    const { routeStack } = this.state

    if (routeStack.length > 1) {
      this.navigateBack()
      return true
    }

    return false
  }

  goBackWrapped = () => {
    if (window.history.length > 1) {
      window.history.go(-1)
    } else {
      this.navigateBack()
    }
  }

  navigateBack = () => {
    const { currentViewOffset, routeStack, baseOffset } = this.state
    const { resetRandomNumbers } = this.props

    const currentRoute = peek(routeStack)
    if (!currentRoute) {
      return
    }

    if (routeStack.length <= 1) {
      return
    }

    const { transition } = currentRoute

    resetRandomNumbers()

    const timing = Animated.timing(currentViewOffset, {
      toValue: baseOffset,
      easing: Easing.bezier(0.3, 1.06, 0.65, 1),
      duration: this.getTiming(transition),
      delay: 0,
      useNativeDriver: true,
    })

    this.notify(peek(routeStack, 1))

    this.setState({
      navigationInProgress: true,
    })

    timing.start(this.postPop)
  }

  handlePasswordReset = () => {
    const { userUsedTemporaryLogin } = this.props
    if (userUsedTemporaryLogin) {
      this.navigate({
        target: TARGET_RESET_PASSWORD,
        transition: transitions.MODAL,
      })
    }
  }

  getParams = (offset = 0) => {
    const { routeStack } = this.state
    const currentRoute = peek(routeStack, offset)

    return currentRoute?.params || {}
  }

  navigate = action => {
    const { currentViewOffset, routeStack, baseOffset } = this.state

    const currentRoute = peek(routeStack)
    if (!currentRoute) {
      return
    }

    const { transition } = action

    if (action.target === NAVIGATION_BACK || action.type === NAVIGATION_BACK) {
      if (Platform.OS === 'web') {
        this.goBackWrapped()
      } else {
        this.navigateBack()
      }
    } else {
      const timing = Animated.timing(currentViewOffset, {
        toValue: baseOffset + 2,
        easing: Easing.bezier(0.3, 1.06, 0.65, 1),
        duration: this.getTiming(transition),
        delay: 0,
        useNativeDriver: true,
      })

      window.setTimeout(() => {
        timing.start(() => {
          this.setState(
            {
              navigationInProgress: false,
            },
            () => this.handlePasswordReset()
          )
        })
      }, 0)

      const newParams = action.params
      const oldParams = currentRoute.params

      this.notify(action)

      const params = { ...oldParams, ...newParams }

      this.setState(state => ({
        baseOffset: baseOffset + 1,
        navigationInProgress: true,
        routeStack: push(state.routeStack, { ...action, params }),
      }))
    }
  }

  resetOffset = baseOffset => {
    this.setState({ baseOffset })
  }

  // Cleanup that happens after back animation finishes
  postPop = () => {
    const { baseOffset, routeStack: oldRouteStack } = this.state

    const [, routeStack] = pop(oldRouteStack)

    this.setState({
      routeStack,
      navigationInProgress: false,
      baseOffset: baseOffset - 1,
    })
  }

  setURL = url => {
    const { initialComponentId } = this.props

    // Prevent url.url from causing error
    if (!url) {
      return
    }

    let { screen, prevScreen, params } = parseURL(
      typeof url === 'object' ? url.url : url
    )

    // This is not a formatted launch URL, so just go to home
    if (!screen) {
      return
    }

    // Catch case with no previous screen
    if (!prevScreen) {
      prevScreen = initialComponentId
    }

    let routeStack = createStack({
      target: prevScreen,
      params: {},
      transition: null,
    })

    routeStack = push(routeStack, {
      target: screen,
      params,
      transition: transitions.PUSH,
    })

    this.setState({
      routeStack,
      baseOffset: -1,
      navigationInProgress: false,
      currentViewOffset: new Animated.Value(0),
    })
  }

  notify = route => {
    const { onNavigate } = this.props

    if (!route || !onNavigate || !route.target) {
      return
    }

    onNavigate({ componentId: route.target })
  }

  componentDidMount() {
    const { skipNotifications, deviceId, resetRandomNumbers } = this.props
    const { routeStack } = this.state
    const { getWebNotificationConfig, updateContext } = this.context

    updateContext({
      getLayoutGuides: () => {
        const { layoutGuides } = this.props
        return layoutGuides
      },
    })

    const currentTarget = peek(routeStack)?.target

    resetRandomNumbers(currentTarget)

    BackHandler.addEventListener('hardwareBackPress', this.goBack)

    Linking.getInitialURL().then(this.setURL)
    this.linkingEventListener = Linking.addEventListener('url', this.setURL)

    // Setup notifications if not defined
    if (!skipNotifications) {
      register(
        {
          onRegister: this.handleRegister,
          onNotification: this.handleNotification,
        },
        getWebNotificationConfig && getWebNotificationConfig()
      )
    } else if (deviceId) {
      this.handleRegister(deviceId, true)
    }
  }

  componentWillUnmount() {
    BackHandler.removeEventListener('hardwareBackPress', this.goBack)

    this.linkingEventListener.remove()
  }

  componentDidUpdate() {
    const { resetRandomNumbers } = this.props

    const { routeStack, navigationInProgress } = this.state

    const currentTarget = peek(routeStack)?.target

    if (navigationInProgress) {
      resetRandomNumbers(currentTarget)
    }
  }

  renderCurrentScreen = () => {
    const { app, layoutGuides, previewType } = this.props
    const { routeStack } = this.state
    const { getParams } = this.context
    const currentRoute = peek(routeStack)
    const currentComponent = app.components[currentRoute.target]

    if (currentRoute.target === TARGET_FORGOT_PASSWORD) {
      return (
        <ForgotPassword
          layoutGuides={layoutGuides}
          datasourceId={Object.keys(app.datasources)[0]}
        />
      )
    }
    if (currentRoute.target === TARGET_RESET_PASSWORD) {
      return (
        <ResetPassword
          layoutGuides={layoutGuides}
          datasourceId={Object.keys(app.datasources)[0]}
        />
      )
    }

    if (!getParams) {
      return <Loading />
    }

    if (currentComponent) {
      return (
        <Screen
          key={currentComponent.id}
          topScreen
          component={currentComponent}
          params={currentRoute.params}
          layoutGuides={layoutGuides}
          previewType={previewType}
          getParams={() => this.getParams(0)}
        />
      )
    }
    return <ErrorScreen message="Screen Missing" />
  }

  render() {
    const { app, layoutGuides, previewType } = this.props
    const { routeStack, currentViewOffset, baseOffset } = this.state
    const { getParams, contextVersion } = this.context
    const isAndroid = this.isAndroid()

    const { width, height } = getScreenSize()

    const currentRoute = peek(routeStack)
    const previousRoute = peek(routeStack, 1)

    const currentRouteIndex = routeStack.length - 1
    const previousRouteIndex = routeStack.length - 2

    if (!currentRoute || !currentRoute.target) {
      window.setTimeout(() => {
        this.setupRoutes()
      }, 0)

      return null
    }

    const transition = currentRoute?.transition || transitions.NONE

    const inputRange = [baseOffset, baseOffset + 1]

    // Get basic styles
    let {
      transformType,
      transformDirection,
      underViewMultiple,
      dimUnderView,
      gap,
    } = TRANSITION_CONFIG[transition]
    gap *= transformDirection

    let transformDistance = transformType === 'translateX' ? width : height
    transformDistance *= transformDirection

    // Cover cases for android push / modal
    let fadeStyles = {}
    if (
      isAndroid &&
      [transitions.MODAL, transitions.PUSH].indexOf(transition) !== -1
    ) {
      fadeStyles = {
        opacity: currentViewOffset.interpolate({
          inputRange,
          outputRange: [0, 1],
        }),
      }

      transformDistance = 100
      transformType = 'translateY'
    }

    const previousComponent =
      previousRoute && app.components[previousRoute.target]

    const previousScreenAnimationStyles = [
      styles.inner,
      {
        transform: [
          {
            [transformType]: currentViewOffset.interpolate({
              inputRange,
              outputRange: [0, -underViewMultiple * transformDistance - gap],
            }),
          },
        ],
      },
    ]

    const currentScreenAnimationStyles = [
      styles.inner,
      {
        ...fadeStyles,
        transform: [
          {
            [transformType]: currentViewOffset.interpolate({
              inputRange,
              outputRange: [transformDistance + gap, 0],
            }),
          },
        ],
      },
    ]

    if (!getParams || !layoutGuides) {
      return <Loading />
    }
    return (
      <View style={styles.wrapper} key={contextVersion}>
        <Animated.View
          style={previousScreenAnimationStyles}
          key={`${previousRouteIndex}-${previousRoute?.target || '0'}`}
        >
          {previousComponent ? (
            <Screen
              key={previousComponent.id}
              topScreen={false}
              component={previousComponent}
              params={previousRoute.params}
              layoutGuides={layoutGuides}
              previewType={previewType}
              getParams={() => this.getParams(1)}
            />
          ) : (
            <ErrorScreen message="Screen Missing" />
          )}
          {dimUnderView && (
            <Animated.View
              key="fader"
              style={[
                styles.inner,
                styles.fader,
                {
                  opacity: currentViewOffset.interpolate({
                    inputRange,
                    outputRange: [0, 1],
                  }),
                },
              ]}
            />
          )}
        </Animated.View>
        <Animated.View
          key={`${currentRouteIndex}-${currentRoute.target || '1'}`}
          style={currentScreenAnimationStyles}
        >
          {this.renderCurrentScreen()}
        </Animated.View>
      </View>
    )
  }
}

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    backgroundColor: '#222',
  },
  inner: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  fader: {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
})

const mapStateToProps = state => ({
  userUsedTemporaryLogin: state.auth.userUsedTemporaryLogin,
})

export default connect(mapStateToProps, {
  showNotification,
  resetRandomNumbers,
})(Navigator)
